import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Eye, EyeOff, Users, Mail, Loader2, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { authApi, companyApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { FooterMinimal } from '../components/Footer';

// Logo component - Always use the official ResaWork logo on login page
const Logo = ({ className = "h-12" }) => {
  return (
    <img 
      src="/logo.png" 
      alt="ResaWork" 
      className={className}
    />
  );
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    license_key: '',
    phone: '',
    address: '',
  });
  
  const [isRegistering, setIsRegistering] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    setIsSendingReset(true);
    try {
      await authApi.requestPasswordReset(forgotEmail, activeTab);
      setResetSent(true);
      toast.success('Lien de réinitialisation envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleLogin = async (role) => {
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await authApi.login({
        email: loginForm.email,
        password: loginForm.password,
        role: role,
      });
      
      const userData = {
        id: response.data.user_id,
        role: response.data.role,
        name: response.data.name,
        company_id: response.data.company_id,
      };
      
      login(userData, response.data.token);
      toast.success(`Bienvenue, ${response.data.name}!`);
      
      // Redirect based on role
      switch (role) {
        case 'company':
          navigate('/company');
          break;
        case 'collaborator':
          navigate('/collaborator');
          break;
        case 'client':
          navigate('/client');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!registerForm.name || !registerForm.email || !registerForm.password || !registerForm.license_key) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setIsLoading(true);
    try {
      await companyApi.register(registerForm);
      toast.success('Entreprise enregistrée avec succès!');
      setIsRegistering(false);
      setLoginForm({ email: registerForm.email, password: registerForm.password });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <Link to="/" className="inline-block">
            <Logo className="h-16 mx-auto mb-4" />
          </Link>
          <p className="text-muted-foreground mt-2">Solution puissante de réservations en ligne</p>
        </div>

        <Card className="shadow-floating border-border/60 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-heading text-xl">
              {isRegistering ? 'Inscription Entreprise' : 'Connexion'}
            </CardTitle>
            <CardDescription>
              {isRegistering 
                ? 'Créez votre compte entreprise avec votre clé de licence'
                : 'Accédez à votre espace de gestion'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {isRegistering ? (
              // Registration Form
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-name">Nom de l'entreprise *</Label>
                  <Input
                    id="reg-name"
                    data-testid="register-name-input"
                    placeholder="Mon Entreprise SARL"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email *</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    data-testid="register-email-input"
                    placeholder="contact@entreprise.fr"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Mot de passe *</Label>
                  <div className="relative">
                    <Input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="register-password-input"
                      placeholder="••••••••"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-license">Clé de licence *</Label>
                  <Input
                    id="reg-license"
                    data-testid="register-license-input"
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={registerForm.license_key}
                    onChange={(e) => setRegisterForm({ ...registerForm, license_key: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-phone">Téléphone</Label>
                  <Input
                    id="reg-phone"
                    data-testid="register-phone-input"
                    placeholder="01 23 45 67 89"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-address">Adresse</Label>
                  <Input
                    id="reg-address"
                    data-testid="register-address-input"
                    placeholder="123 rue de Paris, 75001 Paris"
                    value={registerForm.address}
                    onChange={(e) => setRegisterForm({ ...registerForm, address: e.target.value })}
                  />
                </div>
                
                <Button
                  className="w-full rounded-full mt-4"
                  data-testid="register-submit-btn"
                  onClick={handleRegister}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Inscription...
                    </>
                  ) : (
                    "S'inscrire"
                  )}
                </Button>
                
                <div className="text-center pt-2">
                  <button
                    className="text-sm text-muted-foreground hover:text-primary"
                    onClick={() => setIsRegistering(false)}
                  >
                    Déjà un compte ? Se connecter
                  </button>
                </div>
              </div>
            ) : (
              // Login Form with Tabs (Entreprise, Collaborateur & Client)
              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="company" data-testid="tab-company" className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    <span className="text-xs">Entreprise</span>
                  </TabsTrigger>
                  <TabsTrigger value="collaborator" data-testid="tab-collaborator" className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span className="text-xs">Collaborateur</span>
                  </TabsTrigger>
                  <TabsTrigger value="client" data-testid="tab-client" className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span className="text-xs">Client</span>
                  </TabsTrigger>
                </TabsList>
                
                {['company', 'collaborator', 'client'].map((role) => (
                  <TabsContent key={role} value={role} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor={`${role}-email`}>Email</Label>
                      <Input
                        id={`${role}-email`}
                        type="email"
                        data-testid={`${role}-email-input`}
                        placeholder="votre@email.fr"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={`${role}-password`}>Mot de passe</Label>
                      <div className="relative">
                        <Input
                          id={`${role}-password`}
                          type={showPassword ? 'text' : 'password'}
                          data-testid={`${role}-password-input`}
                          placeholder="••••••••"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && handleLogin(role)}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Lien mot de passe oublié */}
                    <div className="text-right">
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-primary hover:underline"
                        data-testid={`${role}-forgot-password`}
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                    
                    <Button
                      className="w-full rounded-full"
                      data-testid={`${role}-login-btn`}
                      onClick={() => handleLogin(role)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Connexion...
                        </>
                      ) : (
                        'Se connecter'
                      )}
                    </Button>
                    
                    {role === 'company' && (
                      <div className="text-center pt-4 space-y-3">
                        <button
                          className="text-sm text-primary hover:underline"
                          data-testid="register-link"
                          onClick={() => setIsRegistering(true)}
                        >
                          Pas encore de compte ? S'inscrire
                        </button>
                        
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>

        {/* Footer with discreet admin link */}
        <div className="animate-fade-in mt-6" style={{ animationDelay: '200ms' }}>
          <FooterMinimal showAdminLink={true} />
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setForgotEmail('');
          setResetSent(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Mot de passe oublié
            </DialogTitle>
            <DialogDescription>
              Entrez votre adresse email pour recevoir un lien de réinitialisation
            </DialogDescription>
          </DialogHeader>
          
          {resetSent ? (
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Email envoyé !</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Si un compte existe avec l'adresse <span className="font-medium">{forgotEmail}</span>, 
                  vous recevrez un lien de réinitialisation.
                </p>
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowForgotPassword(false)}
              >
                Retour à la connexion
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Adresse email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  data-testid="forgot-email-input"
                  placeholder="votre@email.fr"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1"
                  data-testid="send-reset-btn"
                  onClick={handleForgotPassword}
                  disabled={isSendingReset}
                >
                  {isSendingReset ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    'Envoyer le lien'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
